<template>
  <div class="d-flex flex-wrap align-items-center justify-content-between w-100">
    <div class="flex-grow-1 d-flex justify-content-center">
      <div class="col-12 col-lg-8 col-xl-8 col-xxl-6 col-sm-12">
        <text-field
            v-if="showSearch"
            :model-value="search"
            @update:modelValue="updateSearch"
            :placeholder="$t('object.monitoring.search')"
            clearable
        />
      </div>
    </div>

    <button
        v-if="columnChooser && isMobileActive"
        type="button"
        class="btn ms-2 mb-3 mb-md-3 flex-shrink-0"
        @click.stop="showColumnChooser()"
    >
      <i class="bi bi-list-check"></i>
    </button>

    <button
        v-if="showCreate"
        type="button"
        class="btn btn-primary ms-2 mb-3 mb-md-3 flex-shrink-0"
        @click="handleCreate"
    >
      <i class="bi bi-plus-lg"></i>
      <span class="ms-1 d-none d-md-inline">{{ $t('client.btn-create') }}</span>
    </button>
  </div>

  <div class="d-flex flex-wrap justify-content-end px-2">
    <slot name="filters"></slot>
  </div>

  <div class="table-field" :class="{ 'loading': loading }">
    <div v-if="loading" class="table-loading-overlay">
      <div class="dots-loading">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <div class="table-responsive" :style="{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }">
      <MobileTable
          v-if="isMobileActive"
          :data="paginatedData"
          :columns="columns"
          :visible-columns-state="visibleColumnsState"
          :row-class="typeof rowClass === 'function' ? rowClass : () => ''"
          :cell-class="typeof cellClass === 'function' ? cellClass : () => ''"
          :rowClick="rowClick"
          @row-click="$emit('row-click', $event)"
          @action-click="handleActionClick"
      >
        <template
            v-for="col in columns"
            #[`cell-${col.key}`]="slotProps"
        >
          <slot :name="`cell-${col.key}`" v-bind="slotProps" />
        </template>
      </MobileTable>

      <table v-else class="table table-striped table-hover" :class="{ 'sticky-header': stickyHeader }">
        <TableHeader
            :columns="columns"
            :visibleColumnsState="visibleColumnsState"
            :sortKey="sortKey"
            :sortOrder="sortOrder"
            :lastVisibleColumnKey="lastVisibleColumnKey"
            :columnChooser="columnChooser"
            @sort-change="sortBy"
            @filter-change="handleFilterChange"
            @show-column-chooser="showColumnChooser"
        >
          <template v-for="col in columns" #[`th-${col.key}`]="slotProps">
            <slot :name="`th-${col.key}`" v-bind="slotProps" />
          </template>
        </TableHeader>

        <tbody>
          <tr v-for="(row, rowIndex) in paginatedData" :key="row.id || rowIndex">
            <td
                v-for="(col, colIndex) in columns.filter(c => c && visibleColumnsState[c.key])"
                v-tooltip="getTooltip(row, rowIndex, col, colIndex)"
                :key="col.key"
                :style="{
                  width: col.minWidth || 'auto',
                  maxWidth: col.maxWidth || '100%',
                  textAlign: col.align,
                  backgroundColor: col.key === 'actions' ? '' : (cellClass(row, col) || rowClass(row)),

                  whiteSpace: col.wrap === false ? 'nowrap' : 'normal',
                  overflowWrap: col.wrap === false ? 'normal' : 'anywhere',
                  overflow: 'hidden',
                  textOverflow: col.wrap === false ? 'ellipsis' : 'unset'
                }"
                :class="[{ 'cursor-pointer': rowClick }]"
                :ref="el => setCellRef(el, rowIndex, col, colIndex)"
                @click="handleRowClick(row, col)"
            >
              <template v-if="col.key === 'actions' && col.actions && col.actions.length">
                <div class="d-flex justify-content-end">
                  <button
                      v-for="act in col.actions.filter(a => !a.show || a.show(row))"
                      :key="act.emit"
                      :class="`btn ${typeof act.class === 'function' ? act.class(row) : act.class} me-1`"
                      v-tooltip="{
                        text: typeof act.tooltip === 'function' ? act.tooltip(row) : act.tooltip,
                        theme: {
                          color: '#fff',
                          placement: 'left',
                          backgroundColor: '#000000'
                        }
                      }"
                      @click="handleActionClick(act, row)"
                  >
                    <i v-if="act.icon" :class="typeof act.icon === 'function' ? act.icon(row) : act.icon"></i>
                    <span v-if="act.label">
                      {{ typeof act.label === 'function' ? act.label(row) : act.label }}
                    </span>
                  </button>
                </div>
              </template>

              <template v-else>
                <slot
                    :name="`cell-${col.key}`"
                    :row="row"
                    :value="row[col.key]"
                >
                  {{ row[col.key] }}
                </slot>
              </template>
            </td>
          </tr>

          <tr v-if="!paginatedData.length && !loading">
            <td
                :colspan="columns.filter(c => c && visibleColumnsState[c.key]).length"
                class="text-center py-4 text-muted"
            >
              {{ $t('table-field.no-data') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <Pagination
        v-if="hasParams"
        :current-page="page"
        :total-pages="totalPages"
        :visible-pages="visiblePages"
        @change-page="changePage"
    />
  </div>

  <TableColumnChooser
      ref="showActionColumnChooser"
      :section="section"
      :page-size="pageSize"
      @changeColumns="handleColumnChange"
      @update:pageSize="changePageSize"
  />
</template>

<script src="./TableField.js"></script>
<style src="./TableField.scss" lang="scss" />